export interface Swatch {
  id: string;
  name: string;
  color: { r: number; g: number; b: number };
}

export interface SwatchGroup {
  id: string;
  name: string;
  swatches: Swatch[];
}

export class SwatchesPanel {
  private container: HTMLElement;
  private gridEl: HTMLElement;
  private groups: SwatchGroup[] = [];
  private activeGroupId: string | null = null;
  private selectedSwatchId: string | null = null;
  private onFgColorChange: ((color: { r: number; g: number; b: number }) => void) | null = null;
  private onBgColorChange: ((color: { r: number; g: number; b: number }) => void) | null = null;

  constructor() {
    this.container = document.createElement("div");
    this.container.className = "swatches-panel";
    this.container.innerHTML = `
      <div class="panel-title">
        Swatches
        <div class="swatches-panel__actions">
          <button class="ibtn" id="swatch-add" title="Add swatch">+</button>
          <button class="ibtn" id="swatch-delete" title="Delete swatch">×</button>
          <button class="ibtn" id="swatch-group" title="New group">☰</button>
          <button class="ibtn" id="swatch-import" title="Import">↓</button>
          <button class="ibtn" id="swatch-export" title="Export">↑</button>
        </div>
      </div>
      <div class="swatches-grid" id="swatches-grid"></div>
    `;
    this.gridEl = this.container.querySelector(".swatches-grid")!;
    this._initDefaultSwatches();
    this._bindEvents();
  }

  private _initDefaultSwatches(): void {
    const defaultSwatches: Swatch[] = [
      { id: crypto.randomUUID(), name: "Black", color: { r: 0, g: 0, b: 0 } },
      { id: crypto.randomUUID(), name: "Gray", color: { r: 128, g: 128, b: 128 } },
      { id: crypto.randomUUID(), name: "Dark Gray", color: { r: 64, g: 64, b: 64 } },
      { id: crypto.randomUUID(), name: "Light Gray", color: { r: 192, g: 192, b: 192 } },
      { id: crypto.randomUUID(), name: "White", color: { r: 255, g: 255, b: 255 } },
      { id: crypto.randomUUID(), name: "Red", color: { r: 255, g: 0, b: 0 } },
      { id: crypto.randomUUID(), name: "Orange", color: { r: 255, g: 128, b: 0 } },
      { id: crypto.randomUUID(), name: "Yellow", color: { r: 255, g: 255, b: 0 } },
      { id: crypto.randomUUID(), name: "Green", color: { r: 0, g: 255, b: 0 } },
      { id: crypto.randomUUID(), name: "Cyan", color: { r: 0, g: 255, b: 255 } },
      { id: crypto.randomUUID(), name: "Blue", color: { r: 0, g: 0, b: 255 } },
      { id: crypto.randomUUID(), name: "Magenta", color: { r: 255, g: 0, b: 255 } },
      { id: crypto.randomUUID(), name: "Brown", color: { r: 128, g: 64, b: 0 } },
      { id: crypto.randomUUID(), name: "Pink", color: { r: 255, g: 128, b: 192 } },
      { id: crypto.randomUUID(), name: "Purple", color: { r: 128, g: 0, b: 128 } },
      { id: crypto.randomUUID(), name: "Teal", color: { r: 0, g: 128, b: 128 } },
      { id: crypto.randomUUID(), name: "Navy", color: { r: 0, g: 0, b: 128 } },
      { id: crypto.randomUUID(), name: "Olive", color: { r: 128, g: 128, b: 0 } },
      { id: crypto.randomUUID(), name: "Maroon", color: { r: 128, g: 0, b: 0 } },
      { id: crypto.randomUUID(), name: "Lime", color: { r: 128, g: 255, b: 0 } },
      { id: crypto.randomUUID(), name: "Aqua", color: { r: 0, g: 255, b: 128 } },
      { id: crypto.randomUUID(), name: "Fuchsia", color: { r: 192, g: 0, b: 255 } },
      { id: crypto.randomUUID(), name: "Silver", color: { r: 192, g: 192, b: 192 } },
      { id: crypto.randomUUID(), name: "Gold", color: { r: 255, g: 215, b: 0 } },
    ];
    this.groups.push({ id: crypto.randomUUID(), name: "Default", swatches: defaultSwatches });
    this.activeGroupId = this.groups[0]!.id;
  }

  private _bindEvents(): void {
    this.container.querySelector("#swatch-add")?.addEventListener("click", () => this.addSwatch());
    this.container.querySelector("#swatch-delete")?.addEventListener("click", () => this.deleteLastSwatch());
    this.container.querySelector("#swatch-group")?.addEventListener("click", () => this.createGroup());
    this.container.querySelector("#swatch-import")?.addEventListener("click", () => this.importSwatches());
    this.container.querySelector("#swatch-export")?.addEventListener("click", () => this.exportSwatches());
  }

  setCallbacks(callbacks: {
    onFgColorChange?: (color: { r: number; g: number; b: number }) => void;
    onBgColorChange?: (color: { r: number; g: number; b: number }) => void;
  }): void {
    this.onFgColorChange = callbacks.onFgColorChange || null;
    this.onBgColorChange = callbacks.onBgColorChange || null;
  }

  render(): void {
    const group = this.groups.find(g => g.id === this.activeGroupId);
    if (!group) return;

    this.gridEl.innerHTML = group.swatches.map(s => `
      <div class="swatch-item${s.id === this.selectedSwatchId ? " swatch-item--selected" : ""}" data-id="${s.id}" style="background:rgb(${s.color.r},${s.color.g},${s.color.b})" title="${s.name}"></div>
    `).join("");

    this.gridEl.querySelectorAll(".swatch-item").forEach(el => {
      el.addEventListener("click", (e) => {
        const id = (el as HTMLElement).dataset.id!;
        const swatch = group.swatches.find(s => s.id === id);
        if (!swatch) return;

        const mouseEvent = e as MouseEvent;

        // Alt+click deletes this specific swatch (Photoshop convention),
        // rather than relying solely on the header's delete-last-swatch
        // button (i213).
        if (mouseEvent.altKey) {
          group.swatches = group.swatches.filter(sw => sw.id !== id);
          this.selectedSwatchId = null;
          this.render();
          return;
        }

        this.selectedSwatchId = id;

        // Ctrl/Cmd+click sets the background color; a plain click sets the
        // foreground color (i213 — was previously gated on Alt, which
        // collided with the Alt+click-to-delete convention above).
        if ((mouseEvent.ctrlKey || mouseEvent.metaKey) && this.onBgColorChange) {
          this.onBgColorChange(swatch.color);
        } else if (this.onFgColorChange) {
          this.onFgColorChange(swatch.color);
        }
        this.render();
      });
    });
  }

  addSwatch(color?: { r: number; g: number; b: number }): void {
    const group = this.groups.find(g => g.id === this.activeGroupId);
    if (!group) return;
    const c = color || { r: 128, g: 128, b: 128 };
    group.swatches.push({
      id: crypto.randomUUID(),
      name: `Swatch ${group.swatches.length + 1}`,
      color: c,
    });
    this.render();
  }

  /**
   * Header "delete" button: removes the currently-selected swatch (set via
   * a plain click on a swatch, i213) if one exists in the active group,
   * otherwise falls back to popping the last swatch (prior behavior, kept
   * for when nothing has been selected yet).
   */
  deleteLastSwatch(): void {
    const group = this.groups.find(g => g.id === this.activeGroupId);
    if (!group || group.swatches.length === 0) return;
    if (this.selectedSwatchId) {
      const idx = group.swatches.findIndex(s => s.id === this.selectedSwatchId);
      if (idx !== -1) {
        group.swatches.splice(idx, 1);
        this.selectedSwatchId = null;
        this.render();
        return;
      }
    }
    group.swatches.pop();
    this.render();
  }

  createGroup(): void {
    const name = prompt("Group name:", `Group ${this.groups.length + 1}`);
    if (!name) return;
    const group: SwatchGroup = { id: crypto.randomUUID(), name, swatches: [] };
    this.groups.push(group);
    this.activeGroupId = group.id;
    this.render();
  }

  importSwatches(): void {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,.aco";
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result as string);
          if (Array.isArray(data)) {
            const group: SwatchGroup = {
              id: crypto.randomUUID(),
              name: file.name.replace(/\.[^.]+$/, ""),
              swatches: data.map((c: any, i: number) => ({
                id: crypto.randomUUID(),
                name: c.name || `Swatch ${i + 1}`,
                color: { r: c.r ?? 0, g: c.g ?? 0, b: c.b ?? 0 },
              })),
            };
            this.groups.push(group);
            this.activeGroupId = group.id;
            this.render();
          }
        } catch {
          // Try parsing as ACO (Photoshop swatch format)
          try {
            const bytes = new Uint8Array(reader.result as ArrayBuffer);
            const version = (bytes[0]! << 8) | bytes[1]!;
            const count = (bytes[2]! << 8) | bytes[3]!;
            const swatches: Swatch[] = [];
            let offset = version === 2 ? 4 + count * 4 : 4;
            for (let i = 0; i < count; i++) {
              const colorSpace = (bytes[offset]! << 8) | bytes[offset + 1]!;
              offset += 2;
              if (colorSpace === 0) {
                const r = Math.round(((bytes[offset]! << 8) | bytes[offset + 1]!) / 256);
                const g = Math.round(((bytes[offset + 2]! << 8) | bytes[offset + 3]!) / 256);
                const b = Math.round(((bytes[offset + 4]! << 8) | bytes[offset + 5]!) / 256);
                offset += 10;
                swatches.push({ id: crypto.randomUUID(), name: `Swatch ${i + 1}`, color: { r, g, b } });
              } else {
                offset += 10;
              }
            }
            const group: SwatchGroup = {
              id: crypto.randomUUID(),
              name: file.name.replace(/\.[^.]+$/, ""),
              swatches,
            };
            this.groups.push(group);
            this.activeGroupId = group.id;
            this.render();
          } catch {
            alert("Failed to import swatches.");
          }
        }
      };
      reader.readAsArrayBuffer(file);
    });
    input.click();
  }

  exportSwatches(): void {
    const group = this.groups.find(g => g.id === this.activeGroupId);
    if (!group) return;
    const data = group.swatches.map(s => ({ name: s.name, r: s.color.r, g: s.color.g, b: s.color.b }));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${group.name}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
    this.render();
  }

  destroy(): void {
    this.container.remove();
  }
}
